/**
 * «Game Passport» (owner decision 2026-10-01): a stamp for the
 * first SETTLED stake in every game of the catalog, small token rewards for 5 / 10 / every game, the «Traveller»
 * achievement, the «New» mark in the lobby and the quiet «Try another» hint after a session.
 *
 * No pressure on stakes: a stamp needs one settled round of any size; nothing here ever suggests a bigger bet or a
 * deadline. Tournament hands and training tables write no Bet, so they never stamp.
 *
 * Skill games (catalog.ts, wave A) stamp for a confirmed FINISH, not for a stake: a puzzle competition entry whose
 * solution the server re-played (`skill:completed`, mode `contest`), a chess game for a stake played to the end with
 * at least CHESS_STAMP_MIN_PLIES half-moves (mode `live`). Training never stamps. `stampsOnSettle` tells the server
 * which games still stamp on `bet:settled`.
 */
import { GAME_IDS, type GameId } from './common';
import { SKILL_GAME_IDS } from './catalog';

/** mood tags of the catalog: «Try another» suggests unvisited games sharing the most tags */
export const MOODS = ['chill', 'spicy', 'classic', 'table', 'cn', 'space', 'live', 'strategy', 'puzzle'] as const;
export type Mood = (typeof MOODS)[number];

/**
 * The launch catalog (plan K0–K10). Its games are never «New» for guests: the label is for games added after the
 * launch, during NEW_GAME_DAYS after `addedAt`.
 */
export const CATALOG_LAUNCH = '2026-09-28';
export const NEW_GAME_DAYS = 14;

export interface CatalogMeta {
  /** YYYY-MM-DD (UTC) the game joined the catalog */
  addedAt: string;
  moods: readonly Mood[];
  /**
   * Feature flag «coming soon»: the id is registered (routes, i18n, cover, economy) but the game is not released —
   * the lobby shows the card disabled, nothing links into the game, the passport does not count it in «every game»
   * and «Try another» never suggests it. The wave that ships the game removes this line (and sets `addedAt`).
   */
  comingSoon?: true;
}

/** Every game: when it joined the catalog and its moods. A new game is one line here (and one in GAME_IDS). */
export const GAME_CATALOG: Record<GameId, CatalogMeta> = {
  crash: { addedAt: CATALOG_LAUNCH, moods: ['spicy', 'live', 'space'] },
  pot: { addedAt: CATALOG_LAUNCH, moods: ['live', 'spicy', 'table'] },
  roulette: { addedAt: CATALOG_LAUNCH, moods: ['live', 'classic'] },
  garden: { addedAt: CATALOG_LAUNCH, moods: ['chill', 'classic'] },
  deep: { addedAt: CATALOG_LAUNCH, moods: ['chill', 'spicy'] },
  spaceport: { addedAt: CATALOG_LAUNCH, moods: ['space', 'spicy'] },
  'sweet-shop': { addedAt: CATALOG_LAUNCH, moods: ['chill', 'classic'] },
  workshop: { addedAt: CATALOG_LAUNCH, moods: ['chill', 'strategy'] },
  'koi-pond': { addedAt: '2026-09-29', moods: ['cn', 'spicy'] },
  'lucky-lever': { addedAt: '2026-10-01', moods: ['classic', 'chill', 'cn'] },
  'neon-arcade': { addedAt: '2026-10-01', moods: ['classic', 'spicy', 'space'] },
  'pirate-cove': { addedAt: '2026-10-01', moods: ['classic', 'chill'] },
  carp: { addedAt: '2026-09-29', moods: ['cn', 'strategy', 'spicy'] },
  firecrackers: { addedAt: '2026-09-29', moods: ['cn', 'strategy', 'spicy'] },
  hongbao: { addedAt: '2026-09-29', moods: ['cn', 'live', 'table'] },
  mahjong: { addedAt: '2026-09-29', moods: ['cn', 'strategy', 'classic'] },
  'mahjong-table': { addedAt: '2026-09-30', moods: ['cn', 'table', 'strategy'] },
  zhajinhua: { addedAt: '2026-09-29', moods: ['cn', 'classic', 'strategy'] },
  doudizhu: { addedAt: '2026-09-30', moods: ['cn', 'table', 'strategy'] },
  // skill games (docs/four-games-mobile-plan.md): announced, released by waves B / C / D (drop `comingSoon`, set addedAt)
  'tile-match': { addedAt: '2026-10-01', moods: ['puzzle', 'chill', 'cn'] },
  'color-sort': { addedAt: '2026-10-01', moods: ['puzzle', 'chill'] },
  'daily-words': { addedAt: '2026-10-01', moods: ['puzzle', 'classic'] },
  chess: { addedAt: '2026-10-01', moods: ['strategy', 'classic', 'table'] },
};

/** Is the game released (not behind the `comingSoon` flag)? */
export const isReleased = (g: GameId): boolean => !GAME_CATALOG[g].comingSoon;

/**
 * Games that count in the passport now: the released ones. «Every game» (the `all` reward, «Traveller» gold) is
 * judged against this list at the moment of the grant: 19 before the skill games, 23 once waves B–D release them.
 * A reward already credited is never taken back (its ledger key is spent); a player who had «every game» of 19 simply
 * has 4 more stamps to collect, with no second `all` reward.
 */
export const passportGames = (): GameId[] => GAME_IDS.filter(isReleased);

const DAY_MS = 86_400_000;
const dayMs = (d: string): number => Date.parse(`${d}T00:00:00Z`);

/** «New» for guests: added after the launch and less than NEW_GAME_DAYS ago (a date still ahead counts as new) */
export function isNewGame(game: GameId, now: number = Date.now()): boolean {
  const m = GAME_CATALOG[game];
  if (m.addedAt <= CATALOG_LAUNCH) return false;
  return now - dayMs(m.addedAt) < NEW_GAME_DAYS * DAY_MS;
}

/**
 * «Try another»: up to `n` games the player has not stamped yet, sharing at least one mood with `from`, most shared
 * moods first, then catalog order. Empty when there is nothing new to suggest.
 */
export function similarGames(from: GameId, stamped: ReadonlySet<string>, n = 2): GameId[] {
  const mine = new Set(GAME_CATALOG[from].moods);
  return GAME_IDS.filter((g) => g !== from && !stamped.has(g) && isReleased(g))
    .map((g, i) => ({ g, i, k: GAME_CATALOG[g].moods.filter((m) => mine.has(m)).length }))
    .filter((x) => x.k > 0)
    .sort((a, b) => b.k - a.k || a.i - b.i)
    .slice(0, n)
    .map((x) => x.g);
}

/** Chess: a stake game stamps once it ended (mate, resignation, draw, time) after this many half-moves — no farming by
 * an instant resignation between two accounts. */
export const CHESS_STAMP_MIN_PLIES = 20;

/** Does a settled stake stamp this game? Skill games stamp on a confirmed finish instead (see the header). */
export const stampsOnSettle = (g: GameId): boolean => !(SKILL_GAME_IDS as readonly string[]).includes(g);

/* ---------------- rewards ---------------- */

export const PASSPORT_REWARD_IDS = ['5', '10', 'all'] as const;
export type PassportRewardId = (typeof PASSPORT_REWARD_IDS)[number];

/** lucky amounts, no digit 4; ledger kind `passport_reward`, key `passport:<id>` → once per player */
export const PASSPORT_REWARDS: ReadonlyArray<{ id: PassportRewardId; stamps: number | 'all'; amount: number }> = [
  { id: '5', stamps: 5, amount: 88 },
  { id: '10', stamps: 10, amount: 288 },
  { id: 'all', stamps: 'all', amount: 888 },
];

/** stamps a reward needs with `total` games in the catalog now («all» = every game at the time of the grant) */
export const rewardStamps = (r: (typeof PASSPORT_REWARDS)[number], total: number): number => (r.stamps === 'all' ? total : Math.min(r.stamps, total));

/** rewards reached with `count` stamps of a catalog of `total` games */
export function passportRewardsReached(count: number, total: number): PassportRewardId[] {
  return PASSPORT_REWARDS.filter((r) => count >= rewardStamps(r, total)).map((r) => r.id);
}

/** counters of the «Traveller» achievement (achievements.ts): stamps, and 1 once the passport was full */
export const PASSPORT_COUNTER = 'passport';
export const PASSPORT_FULL_COUNTER = 'passport:full';

/* ---------------- DTOs ---------------- */

export interface PassportStampView {
  game: GameId;
  /** the first settled stake, ISO */
  firstAt: string;
}

export interface PassportRewardView {
  id: PassportRewardId;
  /** stamps needed now (`all` = the catalog size) */
  stamps: number;
  amount: number;
  /** ISO when credited; null — not yet */
  claimedAt: string | null;
}

/** GET /api/me/passport */
export interface PassportResponse {
  /** stamps of games in the catalog, oldest first */
  stamps: PassportStampView[];
  /** games in the catalog now */
  total: number;
  rewards: PassportRewardView[];
}

/** `passport:stamp` — a new stamp (once per game), after the bet's commit */
export interface PassportStampEvent {
  game: GameId;
  firstAt: string;
  count: number;
  total: number;
  /** rewards credited with this stamp (usually none) */
  rewards: Array<{ id: PassportRewardId; amount: number }>;
}

export interface PassportServerToClient {
  'passport:stamp': (e: PassportStampEvent) => void;
}
